'use client';

/**
 * message-tokens — the shared "lego brick" look for message variables and
 * `{{ai}}` blocks (spec §7).
 *
 * One visual system in three places: the insert palette (StepDrawer toolbar),
 * inline inside the message fields (TokenEditor), and the resolved preview
 * (MessagePreview). Raw `{{tokens}}` stay the wire format — resolved
 * server-side by `message-render.service.js` — these helpers only change how
 * they LOOK to recruiters: friendly labels on tinted chips, colored by source
 * (candidate / role / you), with coral reserved for AI blocks.
 */

import { Sparkles } from 'lucide-react';
import { MESSAGE_VARIABLE_LABELS, type AiPartConfig } from './types';
import { parseMessageParts } from './message-parts.js';

export const AI_PROMPT_PLACEHOLDER = 'what should AI write here?';
export const AI_ADV_PLACEHOLDER = 'click to add instructions & examples';

/** Short random id for a new `{{ai2:<id>}}` part — unique within a message. */
export function newAiPartId(): string {
    return `p${Math.random().toString(36).slice(2, 8)}`;
}

/** Chip label for an advanced part: first line of its instructions, clipped. */
export function aiPartLabel(part: AiPartConfig | undefined | null): string {
    const first = (part?.instructions || '').trim().split('\n')[0];
    if (!first) return AI_ADV_PLACEHOLDER;
    return first.length > 48 ? `${first.slice(0, 48)}…` : first;
}

/** Drag-and-drop payload types: bricks carry their token name (or AI prompt)
 *  so editors rebuild them on drop; `text/plain` carries the raw `{{token}}`
 *  for drops outside the app. */
export const DRAG_MIME_VARIABLE = 'application/x-outreach-variable';
export const DRAG_MIME_AI = 'application/x-outreach-ai';
export const DRAG_MIME_AI2 = 'application/x-outreach-ai2';

/* ─── grouping + labels ─── */

export type TokenGroup = 'candidate' | 'role' | 'sender' | 'system';

export function tokenGroup(token: string): TokenGroup {
    if (token.startsWith('candidate.')) return 'candidate';
    if (token.startsWith('job.') || token.startsWith('company.')) return 'role';
    if (token.startsWith('recruiter.') || token.startsWith('calendar.')) return 'sender';
    return 'system';
}

const GROUP_CHIP_CLASS: Record<TokenGroup, string> = {
    candidate: 'ot-chip--candidate',
    role: 'ot-chip--role',
    sender: 'ot-chip--sender',
    system: 'ot-chip--sender',
};

export function variableChipClass(token: string, missing = false): string {
    return `ot-chip ${missing ? 'ot-chip--missing' : GROUP_CHIP_CLASS[tokenGroup(token)]}`;
}

/** Friendly label for any `{{token}}` — falls back to a de-camelled last
 *  segment so unknown tokens never render as code. */
export function tokenLabel(token: string): string {
    const known = (MESSAGE_VARIABLE_LABELS as Record<string, string>)[token];
    if (known) return known;
    const tail = token.split('.').pop() ?? token;
    const words = tail
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ─── parsing ─── */

export type MessagePart =
    | { kind: 'text'; text: string }
    | { kind: 'variable'; token: string }
    | { kind: 'ai'; prompt: string }
    | { kind: 'ai2'; id: string };

export function splitMessageParts(text: string): MessagePart[] {
    return parseMessageParts(text);
}

/* ─── React chips (preview + palettes) ─── */

export function VariableChip({ token, missing, title }: { token: string; missing?: boolean; title?: string }) {
    return (
        <span
            className={variableChipClass(token, missing)}
            title={
                title ??
                (missing
                    ? `${tokenLabel(token)} — no value for this candidate`
                    : `${tokenLabel(token)} — fills in per candidate`)
            }
        >
            <span className="ot-chip-dot" />
            {tokenLabel(token)}
        </span>
    );
}

/** Read-only AI brick for previews — shows the instruction the AI follows.
 *  AI copy is static (written once, same text for everyone) on every channel, so
 *  `perRecipient` defaults to false; pass true only for a genuinely per-candidate field. */
export function AiChip({ prompt, perRecipient = false }: { prompt?: string; perRecipient?: boolean }) {
    const desc = perRecipient
        ? 'AI writes this part per candidate at send time'
        : 'AI writes this part once — same text for everyone';
    return (
        <span className="ot-ai-block ot-ai-block--static" title={prompt ? `${desc} — “${prompt}”` : desc}>
            <span className="ot-ai-tag">
                <Sparkles size={11} strokeWidth={2} /> AI writes
            </span>
            <span className="ot-ai-snippet">
                {prompt || (perRecipient ? 'a personalized line per candidate' : 'one line, same for everyone')}
            </span>
        </span>
    );
}

/** Read-only ADVANCED AI brick for previews — per-prospect generation with
 *  instructions + examples behind it. */
export function Ai2Chip({ part }: { part?: AiPartConfig | null }) {
    const n = (part?.examples || []).filter((e) => e && e.trim()).length;
    const desc = `AI writes this part per candidate at send time — grounded in their sourcing profile when available${
        n ? ` (${n} example${n === 1 ? '' : 's'} guide length & tone)` : ''
    }`;
    return (
        <span className="ot-ai-block ot-ai-block--static ot-ai-block--adv" title={desc}>
            <span className="ot-ai-tag">
                <Sparkles size={11} strokeWidth={2} /> AI writes+
            </span>
            <span className="ot-ai-snippet">{aiPartLabel(part)}</span>
        </span>
    );
}

/* ─── DOM builders (TokenEditor renders chips imperatively inside its
       contentEditable root — React never reconciles in there) ─── */

const SPARKLES_SVG =
    '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>';

export function buildVariableChipEl(doc: Document, token: string): HTMLSpanElement {
    const el = doc.createElement('span');
    el.className = variableChipClass(token);
    el.contentEditable = 'false';
    el.draggable = true;
    el.dataset.token = token;
    el.title = `${tokenLabel(token)} — fills in per candidate. Drag to move, backspace to remove.`;
    const dot = doc.createElement('span');
    dot.className = 'ot-chip-dot';
    el.append(dot, doc.createTextNode(tokenLabel(token)));
    return el;
}

export function buildAiBlockEl(doc: Document, prompt: string, perRecipient = false): HTMLSpanElement {
    const el = doc.createElement('span');
    el.className = 'ot-ai-block';
    el.contentEditable = 'false';
    el.dataset.aiBlock = '';
    const tag = doc.createElement('span');
    tag.className = 'ot-ai-tag';
    // The tag is the drag handle — the box itself stays non-draggable so
    // selecting text in the prompt island doesn't start a drag.
    tag.draggable = true;
    tag.innerHTML = `${SPARKLES_SVG} AI writes`;
    tag.title = perRecipient
        ? 'AI writes this part per candidate at send time. Drag to move.'
        : 'AI writes this part once — same text for everyone. Drag to move.';
    const promptEl = doc.createElement('span');
    promptEl.className = 'ot-ai-prompt';
    promptEl.contentEditable = 'true';
    promptEl.dataset.aiPrompt = '';
    promptEl.dataset.ph = AI_PROMPT_PLACEHOLDER;
    promptEl.spellcheck = false;
    if (prompt) promptEl.textContent = prompt;
    const remove = doc.createElement('button');
    remove.type = 'button';
    remove.className = 'ot-ai-remove';
    remove.dataset.aiRemove = '';
    remove.title = 'Remove AI block';
    remove.textContent = '×';
    remove.tabIndex = -1;
    el.append(tag, promptEl, remove);
    return el;
}

/** Advanced AI block: a CLICKABLE chip (opens the instructions/examples modal
 *  in StepDrawer) — no inline prompt island. Label mirrors the part's
 *  instructions; TokenEditor refreshes it in place when the config changes. */
export function buildAiAdvBlockEl(doc: Document, id: string, part?: AiPartConfig | null): HTMLSpanElement {
    const el = doc.createElement('span');
    el.className = 'ot-ai-block ot-ai-block--adv';
    el.contentEditable = 'false';
    el.dataset.aiAdvBlock = id;
    el.title = 'AI writes this part per candidate at send time. Click to edit instructions & examples.';
    const tag = doc.createElement('span');
    tag.className = 'ot-ai-tag';
    tag.draggable = true;
    tag.innerHTML = `${SPARKLES_SVG} AI writes+`;
    const label = doc.createElement('span');
    label.className = 'ot-ai-adv-label';
    label.dataset.aiAdvLabel = '';
    label.textContent = aiPartLabel(part);
    const remove = doc.createElement('button');
    remove.type = 'button';
    remove.className = 'ot-ai-remove';
    remove.dataset.aiRemove = '';
    remove.title = 'Remove advanced AI block';
    remove.textContent = '×';
    remove.tabIndex = -1;
    el.append(tag, label, remove);
    return el;
}

/** Raw message string → DOM fragment of text nodes + chip elements. */
export function buildMessageFragment(
    doc: Document,
    text: string,
    perRecipient = false,
    aiParts?: Record<string, AiPartConfig>
): DocumentFragment {
    const frag = doc.createDocumentFragment();
    for (const part of splitMessageParts(text)) {
        if (part.kind === 'text') frag.append(doc.createTextNode(part.text));
        else if (part.kind === 'variable') frag.append(buildVariableChipEl(doc, part.token));
        else if (part.kind === 'ai2') frag.append(buildAiAdvBlockEl(doc, part.id, aiParts?.[part.id]));
        else frag.append(buildAiBlockEl(doc, part.prompt, perRecipient));
    }
    return frag;
}

/** One AI block element → its raw `{{ai: prompt}}` form. Braces would break
 *  the server's parse — flattened. */
export function serializeAiBlockEl(el: HTMLElement): string {
    const raw = el.querySelector('[data-ai-prompt]')?.textContent ?? '';
    const prompt = raw.replace(/[{}]/g, ' ').replace(/\s+/g, ' ').trim();
    return prompt ? `{{ai: ${prompt}}}` : '{{ai}}';
}

/** TokenEditor DOM → the raw `{{token}}` string the server resolves. */
export function serializeEditorDom(root: HTMLElement): string {
    let out = '';
    const visit = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            out += node.nodeValue ?? '';
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node as HTMLElement;
        if (el.dataset.oteTrail !== undefined) return;
        if (el.dataset.token) {
            out += `{{${el.dataset.token}}}`;
            return;
        }
        if (el.dataset.aiAdvBlock !== undefined) {
            out += `{{ai2: ${el.dataset.aiAdvBlock}}}`;
            return;
        }
        if (el.dataset.aiBlock !== undefined) {
            out += serializeAiBlockEl(el);
            return;
        }
        if (el.tagName === 'BR') {
            out += '\n';
            return;
        }
        // Browsers wrap lines in divs on some edits — treat as line starts.
        const isBlock = el.tagName === 'DIV' || el.tagName === 'P';
        if (isBlock && out && !out.endsWith('\n')) out += '\n';
        el.childNodes.forEach(visit);
    };
    root.childNodes.forEach(visit);
    return out.replace(/\u00A0/g, ' ').replace(/\n$/, '');
}
